"use client";

import React from "react";
import { Link } from "react-router-dom";
import { Server, Users, Shield, FileText, Key, Settings, TrendingUp, Activity, CheckCircle, AlertCircle, XCircle, ChevronDown } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { useQuery } from "@tanstack/react-query";
import { api } from '@/lib/api';
import { useAuth } from '../context/AuthContext';

interface StatusData {
  hbbs: string;
  hbbr: string;
  key: string;
  timestamp: string;
}

interface DevicesData {
  devices: Array<{ online: boolean }>;
  count: number;
  groups: any[];
}

export function DashboardPage() {
  const { token } = useAuth();

  const { data: status, isLoading: statusLoading } = useQuery({
    queryKey: ["status"],
    queryFn: () => api.getStatus(token!),
    enabled: !!token,
    refetchInterval: 30000,
  });

  const { data: devices, isLoading: devicesLoading } = useQuery({
    queryKey: ["devices"],
    queryFn: () => api.getDevices(token!),
    enabled: !!token,
    refetchInterval: 30000,
  });

  const stats = [
    {
      title: "Total Devices",
      value: devices?.count || 0,
      icon: Server,
      color: "text-blue-500",
      bg: "bg-blue-500/10",
      change: "+12%",
      changeType: "positive",
    },
    {
      title: "Online Devices",
      value: devices?.devices?.filter((d: { online: boolean }) => d.online).length || 0,
      icon: Activity,
      color: "text-green-500",
      bg: "bg-green-500/10",
      change: "+5%",
      changeType: "positive",
    },
    {
      title: "Device Groups",
      value: devices?.groups?.length || 0,
      icon: Users,
      color: "text-purple-500",
      bg: "bg-purple-500/10",
      change: "0%",
      changeType: "neutral",
    },
    {
      title: "Active Policies",
      value: 3,
      icon: Shield,
      color: "text-orange-500",
      bg: "bg-orange-500/10",
      change: "+1",
      changeType: "positive",
    },
  ];

  const statusItems = [
    { label: "hbbs (ID Server)", status: status?.hbbs, icon: Server },
    { label: "hbbr (Relay Server)", status: status?.hbbr, icon: Activity },
  ];

  const getStatusBadge = (statusStr: string | undefined) => {
    if (statusStr === "active") {
      return (
        <Badge variant="success" className="gap-1">
          <CheckCircle className="h-3 w-3" />
          Active
        </Badge>
      );
    }
    if (statusStr === "inactive") {
      return (
        <Badge variant="destructive" className="gap-1">
          <XCircle className="h-3 w-3" />
          Inactive
        </Badge>
      );
    }
    return (
      <Badge variant="warning" className="gap-1">
        <AlertCircle className="h-3 w-3" />
        Unknown
      </Badge>
    );
  };

  if (statusLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i}>
            <CardContent className="pt-6">
              <div className="h-4 w-3/4 animate-pulse rounded bg-muted" />
              <div className="mt-4 h-8 w-1/2 animate-pulse rounded bg-muted" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground">Overview of your RustDesk infrastructure</p>
        </div>
        <Button asChild>
          <Link to="/devices">View All Devices</Link>
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat, i) => (
          <Card key={i}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{stat.title}</p>
                  <p className="text-3xl font-bold">{stat.value}</p>
                  <p className={`text-sm ${stat.changeType === "positive" ? "text-green-500" : stat.changeType === "negative" ? "text-red-500" : "text-muted-foreground"}`}>
                    {stat.change} vs last month
                  </p>
                </div>
                <div className={`${stat.bg} ${stat.color} p-3 rounded-xl`}>
                  <stat.icon className="h-6 w-6" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Server className="h-5 w-5" />
              Service Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {statusItems.map((item, i) => (
                <div key={i} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg bg-muted ${item.status === "active" ? "text-green-500" : "text-red-500"}`}>
                      <item.icon className="h-5 w-5" />
                    </div>
                    <span className="font-medium">{item.label}</span>
                  </div>
                  {getStatusBadge(item.status)}
                </div>
              ))}
              <div className="pt-4 border-t flex items-center justify-between text-sm text-muted-foreground">
                <span>Last updated</span>
                <span>{new Date(status?.timestamp).toLocaleString()}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              Public Key
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="font-mono text-sm bg-muted p-3 rounded break-all">
              {status?.key || "Loading..."}
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              Use this key to configure RustDesk clients
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-2 sm:grid-cols-2">
              <Button asChild variant="outline">
                <Link to="/devices">
                  <Server className="mr-2 h-4 w-4" />
                  Manage Devices
                </Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/groups">
                  <Users className="mr-2 h-4 w-4" />
                  Device Groups
                </Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/policies">
                  <Shield className="mr-2 h-4 w-4" />
                  Access Policies
                </Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/audit-logs">
                  <FileText className="mr-2 h-4 w-4" />
                  Audit Logs
                </Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/api-tokens">
                  <Key className="mr-2 h-4 w-4" />
                  API Tokens
                </Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/client-configs">
                  <Settings className="mr-2 h-4 w-4" />
                  Client Configs
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-sm">
                <div className="p-2 rounded-full bg-green-100 text-green-600">
                  <CheckCircle className="h-4 w-4" />
                </div>
                <div>
                  <p className="font-medium">New device connected</p>
                  <p className="text-muted-foreground">Device {"Laptop-John"} joined group {"Engineering"}</p>
                </div>
                <span className="ml-auto text-xs text-muted-foreground">2 min ago</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <div className="p-2 rounded-full bg-blue-100 text-blue-600">
                  <Shield className="h-4 w-4" />
                </div>
                <div>
                  <p className="font-medium">Policy updated</p>
                  <p className="text-muted-foreground">Default Policy modified by admin</p>
                </div>
                <span className="ml-auto text-xs text-muted-foreground">15 min ago</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <div className="p-2 rounded-full bg-yellow-100 text-yellow-600">
                  <AlertCircle className="h-4 w-4" />
                </div>
                <div>
                  <p className="font-medium">Failed login attempt</p>
                  <p className="text-muted-foreground">IP 192.168.1.100 - Invalid credentials</p>
                </div>
                <span className="ml-auto text-xs text-muted-foreground">1 hour ago</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <div className="p-2 rounded-full bg-purple-100 text-purple-600">
                  <Key className="h-4 w-4" />
                </div>
                <div>
                  <p className="font-medium">API token created</p>
                  <p className="text-muted-foreground">New token {"Monitoring"} with read permissions</p>
                </div>
                <span className="ml-auto text-xs text-muted-foreground">3 hours ago</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}